import React from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useIsAdmin } from '../hooks/useIsAdmin';

import { HttpRenderer } from '../reactvideoeditor/pro/utils/http-renderer';
import { ReactVideoEditor } from '../reactvideoeditor/pro/components/react-video-editor';
import { createPexelsVideoAdaptor } from '../reactvideoeditor/pro/adaptors/pexels-video-adaptor';
import { createPexelsImageAdaptor } from '../reactvideoeditor/pro/adaptors/pexels-image-adaptor';
import { getDefaultAudioAdaptors } from '../reactvideoeditor/pro/adaptors/default-audio-adaptors';
import { createConvexMusicAdaptor, createConvexSfxAdaptor } from '../reactvideoeditor/pro/adaptors/convex-audio-adaptor';
import { SHOW_MOBILE_WARNING } from '../constants';
import { CustomTheme } from '../reactvideoeditor/pro/hooks/use-extended-theme-switcher';
import { MobileWarningModal } from '../reactvideoeditor/pro/components/shared/mobile-warning-modal';
import { ProjectLoadConfirmModal } from '../reactvideoeditor/pro/components/shared/project-load-confirm-modal';
import { useProjectStateFromUrl } from '../reactvideoeditor/pro/hooks/use-project-state-from-url';

export function Editor() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAdmin } = useIsAdmin();
  
  /**
   * Extract project ID from URL query parameter.
   * A project ID represents a unique editing session or workspace for a user.
   * This must match the composition ID defined in the Remotion bundle.
   */
  const projectIdFromUrl = searchParams.get('projectId');
  const PROJECT_ID = projectIdFromUrl || "TestComponent"; // Fallback to default if not provided

  // Redirect to home if no projectId is provided
  React.useEffect(() => {
    if (!projectIdFromUrl) {
      console.warn('[Editor] No projectId in URL, redirecting to home');
      navigate('/');
    }
  }, [projectIdFromUrl, navigate]);

  /**
   * Load project state from API via URL parameter.
   * Landing site saves project: overlays sent to API
   * Then navigates to: /editor?projectId=456
   * 
   * This hook fetches the project state and returns overlays, aspect ratio, and loading state.
   * If there's existing autosave data, user will be prompted via modal to choose.
   */
  const { overlays, aspectRatio, isLoading, showModal, onConfirmLoad, onCancelLoad } = 
    useProjectStateFromUrl('projectId', PROJECT_ID);

  // Handle theme changes
  const handleThemeChange = (themeId: string) => {
    console.log('Theme changed to:', themeId);
    // You can add additional theme change logic here
  };

    // Define available themes - you can add more custom themes here
    const availableThemes: CustomTheme[] = [];

  // Memoize audio adaptors based on admin status
  const audioAdaptors = React.useMemo(() => {
    const defaultAdaptors = getDefaultAudioAdaptors();
    if (isAdmin) {
      return [
        ...defaultAdaptors,
        createConvexMusicAdaptor(),
        createConvexSfxAdaptor(),
      ];
    }
    return defaultAdaptors;
  }, [isAdmin]);

   // Default renderer uses Express API routes (EC2 backend)
   const rendererBaseUrl = import.meta.env.VITE_RENDERER_URL || '';
   const ssrRenderer = React.useMemo(() =>
    new HttpRenderer(`${rendererBaseUrl}/api/latest/ssr`, {
      type: 'ssr',
      entryPoint: `${rendererBaseUrl}/api/latest/ssr`
    }), [rendererBaseUrl]
  );

  // const lambdaRenderer = React.useMemo(() => 
  //   new HttpRenderer('/api/latest/lambda', {
  //     type: 'lambda',
  //     entryPoint: '/api/latest/lambda'
  //   }), []
  // );

  return (
    <div className="w-full h-full fixed inset-0">
      <MobileWarningModal show={SHOW_MOBILE_WARNING} />
      <ProjectLoadConfirmModal 
        isVisible={showModal}
        onConfirm={onConfirmLoad}
        onCancel={onCancelLoad}
      />
      {projectIdFromUrl ? (
        <ReactVideoEditor
          projectId={PROJECT_ID}
          defaultOverlays={overlays as any}
          defaultAspectRatio={aspectRatio || "9:16"}
          isLoadingProject={isLoading}
          fps={30}
          renderer={ssrRenderer}
          disabledPanels={[]}
          availableThemes={availableThemes}
          defaultTheme="dark"
          adaptors={{
            video: [createPexelsVideoAdaptor((import.meta as any).env?.VITE_PEXELS_API_KEY || 'CEOcPegZJRoNztih7auwNoFZmIFTmlYoZTI0NgTRCUxkFhXORBhERORM')],
            images: [createPexelsImageAdaptor((import.meta as any).env?.VITE_PEXELS_API_KEY || 'CEOcPegZJRoNztih7auwNoFZmIFTmlYoZTI0NgTRCUxkFhXORBhERORM')],
            audio: audioAdaptors,
          }}
          onThemeChange={handleThemeChange}
          showDefaultThemes={true}
          sidebarWidth="clamp(350px, 25vw, 500px)"        
          sidebarIconWidth="57.6px"
          showIconTitles={false}
        />
      ) : (
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <p className="text-gray-500 mb-4">No project selected</p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
            >
              Go to Home
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

